import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useCredit } from '../../config/creditContext';
import { useAuthContext } from '@asgardeo/auth-react';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';

const Account = () => {
  const { email, firstName, fullName, balance } = useCredit();
  const { state, getBasicUserInfo } = useAuthContext();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadUserData = async () => {
      try {
        setLoading(true);

        const sessionData = sessionStorage.getItem('user');
        if (sessionData) {
          const parsedData = JSON.parse(sessionData);
          setUserData(parsedData);
        }

        if (state?.isAuthenticated) {
          try {
            const userInfo = await getBasicUserInfo();
            if (userInfo) {
              setUserData(userInfo);
            }
          } catch (error) {
            console.error('Failed to get user info from Asgardeo:', error);
          }
        }
      } catch (error) {
        console.error('Error loading user data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadUserData();
  }, [state?.isAuthenticated, getBasicUserInfo]);

  const getDisplayName = () => {
    if (fullName && fullName.trim()) return fullName;
    if (firstName && firstName.trim()) return firstName;
    if (userData?.given_name && userData?.family_name)
      return `${userData.given_name} ${userData.family_name}`;
    if (userData?.given_name) return userData.given_name;
    if (userData?.name) return userData.name;
    if (userData?.first_name) return userData.first_name;
    if (userData?.family_name) return userData.family_name;
    if (userData?.sub) return userData.sub.split('@')[0];
    return 'User';
  };

  const getDisplayEmail = () => {
    if (email && email.trim()) return email;
    if (userData?.email) return userData.email;
    return 'Loading...';
  };

  const displayName = getDisplayName();
  const displayEmail = getDisplayEmail();

  return (
    <div className="min-h-screen flex flex-col bg-[#fafafd]">
      <Header />
      <section className="py-8 flex-grow">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="bg-white p-8 rounded-2xl shadow-md border">
            <Helmet>
              <title>My Account | paralegal.lk</title>
              <meta name="robots" content="noindex" />
            </Helmet>

            <div className="mb-8 bg-white rounded-xl shadow-sm p-6 md:p-8">
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900">
                Welcome,{' '}
                {loading ? (
                  <span className="inline-block w-32 h-8 bg-gray-200 rounded animate-pulse" />
                ) : (
                  <span className="text-pl_prim_color_1">{displayName}!</span>
                )}
              </h1>
              <p className="mt-2 text-lg text-gray-600">
                Manage your account settings and Free Credit details
              </p>
            </div>

            <div className="grid gap-8 md:grid-cols-2">
              <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="p-6 md:p-8">
                  <h2 className="flex items-center text-xl font-semibold text-gray-900 mb-6">
                    <svg className="w-[24px] h-[24px] mr-2 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    Personal Details
                  </h2>
                  <div className="space-y-4">
                    <div className="flex items-center py-2">
                      <span className="text-sm font-medium text-gray-500 w-24">Name</span>
                      <span className="text-gray-900">
                        {loading ? (
                          <span className="inline-block w-32 h-4 bg-gray-200 rounded animate-pulse" />
                        ) : (
                          displayName
                        )}
                      </span>
                    </div>
                    <div className="flex items-center py-2">
                      <span className="text-sm font-medium text-gray-500 w-24">Email</span>
                      <span className="text-gray-900">
                        {loading ? (
                          <span className="inline-block w-48 h-4 bg-gray-200 rounded animate-pulse" />
                        ) : (
                          displayEmail
                        )}
                      </span>
                    </div>
                  </div>
                  <div className="mt-8">
                    <button
                      onClick={() =>
                        (window.location.href = `https://accounts.asgardeo.io/t/paralegallk/accountrecoveryendpoint/recoveraccountrouter.do?client_id=${process.env.REACT_APP_ASGARDEO_CLIENT_ID}&redirect_uri=${process.env.REACT_APP_SIGN_IN_URL}`)
                      }
                      className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-br from-[#dd0c7e] via-[#ca1582] to-[#a6208e] hover:bg-gradient-to-br hover:from-[#a6208e] hover:via-[#ca1582] hover:to-[#dd0c7e] transition duration-500 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#dd0c7e]"
                    >
                      Reset Password
                    </button>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="p-6 md:p-8">
                  <h2 className="flex items-center text-xl font-semibold text-gray-900 mb-6">
                    <svg className="w-[24px] h-[24px] mr-2 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                    </svg>
                    Free Credit Details
                  </h2>
                  <div className="space-y-4">
                    <div className="flex items-center py-2">
                      <span className="text-sm font-medium text-gray-500 w-24">Plan</span>
                      <span className="text-gray-900">Free</span>
                    </div>
                    <div className="flex items-center py-2">
                      <span className="text-sm font-medium text-gray-500 w-24">Balance</span>
                      <span className="text-gray-900">
                        {balance !== null ? (
                          `Rs. ${balance}`
                        ) : (
                          <span className="inline-block w-16 h-4 bg-gray-200 rounded animate-pulse" />
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Footer fix */}
      <div className="mt-auto">
        <Footer />
      </div>
    </div>
  );
};

export default Account;
